/**
 * Чистая логика экрана цикла (`CycleDetail`), вынесенная ради проверки
 * (agroai-6al.27): на каноне три блока экрана говорили не то, что значат
 * данные.
 *
 * 1. «Окон идёт сейчас». Сводка циклов канона поле `works_active` не отдаёт
 *    (это разрез кампаний, а не цикла), и карточка печатала «—» с пояснением
 *    «цикл закрыт … — открытых окон нет» у идущего цикла. Число идущих окон
 *    уже есть на экране: это работы цикла, положение которых относительно
 *    среза посчитано адаптером (`works.ts: statusOf`). Считать их здесь — не
 *    свёртка корпуса в браузере, а счёт строк, которые экран и так рисует.
 * 2. «Поздние». Канон опоздание отчёта не считает; прежде адаптер клал в это
 *    поле «после срока» (окно закрыто, отчёта НЕТ) — другую величину под
 *    чужой подписью. Теперь у работы канона есть своё поле `overdue`, и
 *    колонка называется тем, что в ней лежит.
 * 3. Шкала хронологии. Работа без окна приезжает с пустыми датами, и пустая
 *    строка как «самая ранняя дата» обнуляла шкалу: экран писал «Хронология
 *    не построена» над сводкой «37 работ · 32 закрыто…».
 */
import type { Cycle, Work } from '../data/types';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';
import { dateShort } from '../lib/format';

const m = defineMessages({
  closed: {
    id: 'cycle.view.windows.closed',
    defaultMessage: 'агроцикл закрыт {date} — открытых окон нет',
    description: 'Подпись под KPI «Окон идёт сейчас» у закрытого агроцикла; {date} — дата закрытия',
  },
  notRead: {
    id: 'cycle.view.windows.notRead',
    defaultMessage: 'работы агроцикла ещё не прочитаны',
    description: 'Подпись под KPI «Окон идёт сейчас»: работы агроцикла ещё не загрузились, посчитать нечего',
  },
  namesNotRead: {
    id: 'cycle.view.windows.namesNotRead',
    defaultMessage: 'названия идущих работ ещё не прочитаны',
    description: 'Подпись под KPI «Окон идёт сейчас»: число окон известно из сводки, а названия работ ещё не загрузились',
  },
  next: {
    id: 'cycle.view.windows.next',
    defaultMessage: 'сейчас окон нет; следующее — «{name}» с {date}',
    description: 'Подпись под KPI «Окон идёт сейчас»: идущих окон нет; {name} — название ближайшей работы плана, {date} — дата начала её окна',
  },
  noneAhead: {
    id: 'cycle.view.windows.noneAhead',
    defaultMessage: 'сейчас окон нет, впереди окон не назначено',
    description: 'Подпись под KPI «Окон идёт сейчас»: ни идущих, ни будущих окон',
  },
});

/** Границы шкалы по окнам цикла и работ; работы без окна в границы не входят. */
export function scaleBounds(
  works: readonly Work[],
  cycleWindow: { from: string; to: string } | null,
): { min: string; max: string } | null {
  let min: string | null = cycleWindow?.from || null;
  let max: string | null = cycleWindow?.to || null;
  for (const work of works) {
    if (!work.window.from || !work.window.to) continue;
    if (min === null || work.window.from < min) min = work.window.from;
    if (max === null || work.window.to > max) max = work.window.to;
  }
  return min && max ? { min, max } : null;
}

export interface ActiveWindows {
  /** Число работ с идущим окном; `null` — не из чего посчитать. */
  value: number | null;
  /** Подпись карточки: названия идущих работ или почему их нет. */
  note: string;
}

/**
 * Идущие окна цикла. Работы прочитаны — считаем по ним (у обоих источников
 * статус работы — положение среза относительно её окна); не прочитаны —
 * берём число сводки, если источник его отдал.
 *
 * Пояснение «цикл закрыт» печатается ТОЛЬКО у закрытого цикла: у идущего без
 * открытых окон сказано, какое окно следующее.
 */
export function activeWindows(cycle: Cycle, works: readonly Work[] | null): ActiveWindows {
  const fromSummary = cycle.progress.works_active;
  const active = works ? works.filter((w) => w.status === 'active') : null;
  const value = active ? active.length : Number.isFinite(fromSummary) ? fromSummary : null;

  if (active && active.length > 0) return { value, note: active.map((w) => w.name).join(', ') };
  if (cycle.status === 'closed') {
    return { value, note: tr(m.closed, { date: dateShort(cycle.window.to) }) };
  }
  if (value === null) return { value, note: tr(m.notRead) };
  if (value > 0) return { value, note: tr(m.namesNotRead) };
  const next = (works ?? [])
    .filter((w) => w.status === 'upcoming' && w.window.from)
    .sort((a, b) => a.window.from.localeCompare(b.window.from))[0];
  return {
    value,
    note: next ? tr(m.next, { name: next.name, date: dateShort(next.window.from) }) : tr(m.noneAhead),
  };
}

/**
 * Что показывает последняя колонка таблицы работ. Источник, отдающий
 * «после срока» (канон), получает колонку с этим именем; источник с
 * опозданием отчётов (фикстуры) — «Поздние». Смешанной таблицы не бывает:
 * работы цикла приходят одним набором одного источника.
 */
export function lateColumn(works: readonly Work[]): 'overdue' | 'late' {
  return works.some((w) => w.totals.overdue !== undefined) ? 'overdue' : 'late';
}
